import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    FiUserCheck,
    FiArrowLeft,
    FiSearch,
    FiUserPlus,
} from "react-icons/fi";
import Switch from "@mui/material/Switch";
import {
    PageHeader,
    StatusBadge,
    EmptyState,
    SkeletonTableRow,
    useToast,
} from "../../theme/components";
import { PageButton } from "../../theme/components/PageButton";
import { RegIdChip } from "../../theme/components/RegIdChip";
import { useAuth } from "../../auth/AuthContext";
import { adminApi } from "../../api/endpoints/admin";
import { formatDateTimeDisplay } from "../../utils/dates";
import "./AdminUsers.css";

const ROLE_SEVERITY = {
    admin: "info",
    staff: "warning",
    user: "neutral",
};

const formatDate = (iso) => formatDateTimeDisplay(iso, { fallback: 'Never' });

const COLUMNS = [
    { key: "userId", label: "User ID", width: "120px" },
    { key: "email", label: "Email" },
    { key: "name", label: "Name" },
    { key: "role", label: "Role", width: "110px" },
    { key: "lastLogin", label: "Last Login", width: "170px" },
    { key: "status", label: "Status", width: "120px" },
    { key: "actions", label: "", width: "120px" },
];

const AdminUsers = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState(null);
    const [error, setError] = useState(null);
    const [query, setQuery] = useState("");
    const [busyId, setBusyId] = useState(null);

    const load = async () => {
        try {
            const list = await adminApi.listUsers();
            setUsers(list);
        } catch (e) {
            setError(
                e?.response?.data?.message || e?.message || "Failed to load users"
            );
        }
    };

    useEffect(() => {
        load();
    }, []);

    const handleToggle = async (u) => {
        if (u.id === currentUser?.id) return; // self-disable prevented server-side too
        if (busyId) return;
        setBusyId(u.id);
        try {
            const next = await adminApi.toggleActive(u.id, !u.isActive);
            setUsers((prev) => (prev ? prev.map((x) => (x.id === next.id ? next : x)) : prev));
            toast.success(`${next.email} is now ${next.isActive ? "active" : "disabled"}.`);
        } catch (e) {
            const msg = e?.response?.data?.message || e?.message || "Failed to update user";
            toast.error(msg);
        } finally {
            setBusyId(null);
        }
    };

    const filtered = (users || []).filter((u) => {
        const q = query.trim().toLowerCase();
        if (!q) return true;
        return (
            (u.email || "").toLowerCase().includes(q) ||
            (u.fullName || "").toLowerCase().includes(q) ||
            (u.role || "").toLowerCase().includes(q)
        );
    });

    return (
        <div className="admin-users-wrapper">
            <PageHeader
                title="Users"
                subtitle="Manage Pediatrack user accounts and access"
                icon={FiUserCheck}
                actions={
                    <>
                        <PageButton
                            type="button"
                            variant="primary"
                            startIcon={<FiUserPlus size={16} />}
                            onClick={() => navigate("/admin/users/new")}
                        >
                            Add user
                        </PageButton>
                        <PageButton
                            type="button"
                            variant="ghost"
                            startIcon={<FiArrowLeft size={16} />}
                            onClick={() => navigate("/admin")}
                        >
                            Admin
                        </PageButton>
                    </>
                }
            />

            <div className="admin-users-body">
                <div className="admin-users-search">
                    <FiSearch size={14} />
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search email, name, role…"
                        aria-label="Search users"
                    />
                </div>

                {error ? (
                    <EmptyState
                        illustration="error"
                        title="Couldn't load users"
                        description={error}
                        action={
                            <PageButton
                                type="button"
                                variant="primary"
                                onClick={() => {
                                    setError(null);
                                    load();
                                }}
                            >
                                Try again
                            </PageButton>
                        }
                    />
                ) : (
                    <div className="admin-users-card">
                        <div className="admin-users-table-wrap">
                            <table className="admin-users-table">
                                <thead>
                                    <tr>
                                        {COLUMNS.map((c) => (
                                            <th key={c.key} style={{ width: c.width }}>
                                                {c.label}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {users === null ? (
                                        Array.from({ length: 4 }).map((_, i) => (
                                            <SkeletonTableRow
                                                key={i}
                                                cols={COLUMNS.length}
                                            />
                                        ))
                                    ) : filtered.length === 0 ? (
                                        <tr>
                                            <td colSpan={COLUMNS.length} className="admin-users-empty">
                                                <EmptyState
                                                    illustration={query ? "search" : "no-data"}
                                                    compact
                                                    title={
                                                        query
                                                            ? "No users match your search"
                                                            : "No users yet"
                                                    }
                                                />
                                            </td>
                                        </tr>
                                    ) : (
                                        filtered.map((u) => {
                                            const isSelf = u.id === currentUser?.id;
                                            return (
                                                <tr key={u.id}>
                                                    <td>
                                                        {u.userIdCode ? (
                                                            <RegIdChip
                                                                value={u.userIdCode}
                                                                label="PHM"
                                                            />
                                                        ) : (
                                                            <span className="admin-users-empty-id">
                                                                —
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span className="admin-users-email">
                                                            {u.email}
                                                            {isSelf && (
                                                                <span className="admin-users-self">
                                                                    you
                                                                </span>
                                                            )}
                                                        </span>
                                                    </td>
                                                    <td>{u.fullName || "—"}</td>
                                                    <td>
                                                        <StatusBadge
                                                            severity={ROLE_SEVERITY[u.role] || "neutral"}
                                                        >
                                                            {u.role}
                                                        </StatusBadge>
                                                    </td>
                                                    <td>{formatDate(u.lastLoginAt)}</td>
                                                    <td>
                                                        <StatusBadge
                                                            severity={u.isActive ? "success" : "danger"}
                                                        >
                                                            {u.isActive ? "active" : "disabled"}
                                                        </StatusBadge>
                                                    </td>
                                                    <td>
                                                        <div className="admin-users-toggle">
                                                            <Switch
                                                                size="small"
                                                                checked={!!u.isActive}
                                                                onChange={() => handleToggle(u)}
                                                                disabled={isSelf || busyId === u.id}
                                                                inputProps={{
                                                                    "aria-label": `Toggle ${u.email}`,
                                                                }}
                                                            />
                                                            {isSelf && (
                                                                <span className="admin-users-toggle-hint">
                                                                    self
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminUsers;
